import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  applyAppSettings,
  DEFAULT_SETTINGS,
  getPaletteTokens,
  isCustomSettings,
  loadSettings,
  saveSettings,
} from './appSettings';

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    const initial = loadSettings();
    applyAppSettings(initial);
    return initial;
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    applyAppSettings(settings);
  }, [settings]);

  const updateSettings = useCallback((patch) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      saveSettings(next);
      return next;
    });
  }, []);

  const resetSettings = useCallback(() => {
    saveSettings(DEFAULT_SETTINGS);
    setSettings({ ...DEFAULT_SETTINGS });
  }, []);

  const tokens = useMemo(() => getPaletteTokens(settings), [settings]);

  const value = useMemo(
    () => ({
      settings,
      tokens,
      drawerOpen,
      isCustom: isCustomSettings(settings),
      openDrawer: () => setDrawerOpen(true),
      closeDrawer: () => setDrawerOpen(false),
      updateSettings,
      resetSettings,
    }),
    [settings, tokens, drawerOpen, updateSettings, resetSettings]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useAppSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useAppSettings must be used within SettingsProvider');
  return ctx;
}
